/**
 * Declarative step list for the new-trip spotlight walkthrough — see
 * ADR-0091.
 *
 * Pure module (no JSX) so the engine's progression and the step
 * invariants are unit-testable in isolation, mirroring the
 * `consent-sheet-contract` pattern: the constant IS the contract.
 *
 * Each step names a `targetKey` (the id a `useTourTarget(key)` ref
 * registers under) and the `tab` the engine switches to before
 * measuring. The tour deliberately never opens another overlay
 * (SpeedDial menu / edit panel) — those would fight the tour overlay's
 * portal z-order — so steps spotlight the *trigger* control and the
 * bubble copy describes what it opens. A `null` `targetKey` renders a
 * centered bubble over the dimmed tab — used for surfaces that don't
 * exist yet on a brand-new trip (no place cards, empty wishlist) or
 * live deep inside a panel, where a spotlight would have nothing
 * stable to anchor to.
 */

import { isLayersFabEnabled } from '../../lib/feature-flags';

/** Tab indices match `TABS = ['Discover', 'Plan', 'Review']` in the
 *  trip-detail screen. */
export type TourTab = 0 | 1 | 2;

/** A declarative instruction the overlay emits and the trip screen
 *  consumes to position the next step. The trip screen owns the mapping
 *  from this shape to its local tab state. The tour only ever switches
 *  tabs — it never opens menus/panels (see module header). */
export interface TourCommand {
  /** Switch to this tab. Omitted = leave the current tab. */
  tab?: TourTab | undefined;
}

export interface TourStep {
  /** Stable, unique id (also the analytics key). */
  id: string;
  /** Registry key of the element to spotlight, or `null` for a
   *  centered bubble over the dimmed tab. */
  targetKey: string | null;
  /** Tab the engine switches to before measuring this step. */
  tab: TourTab;
  title: string;
  body: string;
  /** Bubble placement hint relative to the target. `auto` flips
   *  above/below to fit; `center` ignores the target and centers. */
  placement?: 'auto' | 'top' | 'bottom' | 'center' | undefined;
}

/**
 * Ordered tour. Grouped by tab to minimise tab-switch thrash:
 * Discover → Plan → Review, with the cross-cutting Share step last
 * (its trigger lives in the header, visible on every tab).
 *
 * Not exported directly — `TOUR_STEPS` below is this list minus any
 * step whose target is disabled for this build.
 */
const ALL_TOUR_STEPS: readonly TourStep[] = [
  {
    id: 'discover.wishlist',
    targetKey: 'discover.wishlistSection',
    tab: 0,
    title: 'Your wishlist',
    body: 'Places you want to see live here until you slot them into a day. Think of it as a shortlist for the trip.',
    placement: 'auto',
  },
  {
    id: 'discover.addWishlist',
    targetKey: 'discover.addWishlistBtn',
    tab: 0,
    title: 'Save a place',
    body: 'Tap to search and add a restaurant, sight, or hotel to your wishlist — no need to decide which day yet.',
    placement: 'auto',
  },
  {
    id: 'plan.addFromWishlist',
    targetKey: null,
    tab: 0,
    title: 'Schedule it later',
    body: 'When you are ready, each wishlist card has an "Add to day" button that drops it onto the timeline.',
    placement: 'center',
  },
  {
    id: 'plan.addFab',
    targetKey: 'fab.add',
    tab: 1,
    title: 'Add to a day',
    body: 'The + button opens the add sheet on whichever day you are viewing — search for a place to visit, or switch to Journey for a flight, train or transfer.',
    placement: 'auto',
  },
  {
    id: 'plan.layers',
    targetKey: 'fab.layers',
    tab: 1,
    title: 'Toggle layers',
    body: 'The layers button shows or hides weather, opening hours, transport connectors, and photos to keep the timeline as dense as you like.',
    placement: 'auto',
  },
  {
    id: 'plan.editPlace',
    targetKey: null,
    tab: 1,
    title: 'Tweak any stop',
    body: 'Tap a place on the timeline to edit its times, cost, and notes, or to swap the transport mode of a connector.',
    placement: 'center',
  },
  {
    id: 'plan.editTrip',
    targetKey: 'header.editTrip',
    tab: 1,
    title: 'Edit trip details',
    body: 'Rename the trip, change the destination, or adjust the date range here. Days are added or trimmed to match.',
    placement: 'auto',
  },
  {
    id: 'plan.participants',
    targetKey: 'header.editTrip',
    tab: 1,
    title: 'Invite travellers',
    body: 'The same edit panel manages participants — add the people on the trip so costs can be split between them.',
    placement: 'auto',
  },
  {
    id: 'plan.notes',
    targetKey: null,
    tab: 1,
    title: 'Add notes',
    body: 'Attach free-form notes to the trip, a day, or any place — confirmation numbers, tips, anything worth remembering.',
    placement: 'center',
  },
  {
    id: 'plan.todos',
    targetKey: null,
    tab: 1,
    title: 'Track to-dos',
    body: 'Searching for something the map does not know? Use your own words instead — it lands as a loose block you can firm up later.',
    placement: 'center',
  },
  {
    id: 'plan.costs',
    targetKey: null,
    tab: 1,
    title: 'Split costs',
    body: 'Give a place or entry a cost and assign who paid — Kiips tallies the settlement so everyone settles up fairly.',
    placement: 'center',
  },
  {
    id: 'review.itinerary',
    targetKey: null,
    tab: 2,
    title: 'Review the whole trip',
    body: 'The Review tab lays out every day end to end — a clean, read-only overview of the finished itinerary.',
    placement: 'center',
  },
  {
    id: 'review.print',
    targetKey: null,
    tab: 2,
    title: 'Print or export',
    body: 'The share button up top can print the itinerary, export it to a file, or subscribe to it in your calendar.',
    placement: 'center',
  },
  {
    id: 'share.trip',
    targetKey: 'share.shareBtn',
    tab: 1,
    title: 'Share with anyone',
    body: 'Invite a co-traveller to view or edit the trip with you. That is the tour — enjoy planning!',
    placement: 'auto',
  },
];

/**
 * The live tour. Steps whose spotlight target isn't rendered in this
 * build are dropped rather than shown against a missing anchor — the
 * overlay would fall back to a centered bubble describing a control
 * the user can't see. Today that's only `plan.layers`, gated on
 * `EXPO_PUBLIC_SHOW_LAYERS_FAB` (see `isLayersFabEnabled`).
 *
 * The flag is a build-time constant, so this filter runs once at
 * module load and every downstream consumer (store progression, the
 * "N of M" counter, tests) stays consistent with what's on screen.
 */
export const TOUR_STEPS: readonly TourStep[] = ALL_TOUR_STEPS.filter(
  (step) => step.id !== 'plan.layers' || isLayersFabEnabled(),
);

/** Total step count — exported so the bubble's "N of M" counter and
 *  tests reference one source. */
export const TOUR_STEP_COUNT = TOUR_STEPS.length;
